'use client';
import { useState, useRef, useLayoutEffect } from 'react';
import WindowSize from '@/utils/windowSize';
import ReactPlayer from 'react-player/lazy';
import Image from 'next/image';
import { SvgPause } from '@/components/Icons/SvgPause';
import { SvgPlay } from '@/components/Icons/SvgPlay';

export default function VideoLargeAutoplay({ block, position }) {
  const {
    desktop_video_link,
    mobile_video_link,
    mobile_placeholder_image,
    desktop_placeholder_image,
    top_padding_off,
    _metadata,
    use_native_controls,
    autoplay,
    headline,
    headline_position
  } = block.video_large_autoplay;

  const [videoPlaying, setVideoPlaying] = useState(!!autoplay);
  const [videoLoaded, setVideoLoaded] = useState(false);
  const [remountKey, setRemountKey] = useState(0);
  const videoRef = useRef();
  const windowSize = WindowSize();

  // Bump the key to remount on Activity show, since Vimeo keeps
  // trying to pause itself otherwise. Also reset back to the autoplay default.
  useLayoutEffect(() => {
    return () => {
      setVideoPlaying(!!autoplay);
      setVideoLoaded(false);
      setRemountKey((key) => key + 1);
    };
  }, [autoplay]);

  //play or pause video on icon click
  function handleClick() {
    if (videoRef.current) {
      if (videoPlaying) {
        setVideoPlaying(false);
      } else {
        setVideoPlaying(true);
      }
    }
  }

  const configuration = {
    loop: true,
    playing: videoPlaying,
    playsinline: true,
    volume: 0,
    muted: true,
    controls: !!use_native_controls,
    width: '100%',
    height: '100%',
    onPlay: () => {
      // When autoplay is on, the poster image will disappear too early due to
      // buffering, causing a flicker effect. To prevent this, we only consider
      // the video truly loaded when the video is actually ready to play.
      if (autoplay) {
        setVideoLoaded(true);
      }
    },
    onReady: () => {
      // When we have autoplay off, we don't have the flicker effect mentioned
      // above, so we can set the videoLoaded to true immediately when ready.
      if (!autoplay) {
        setVideoLoaded(true);
      }
    },
    config: {
      vimeo: {
        playerOptions: {
          unmute_button: false // Disable floating unmute button
        }
      }
    }
  };

  return (
    <section
      id={_metadata.uid + '_' + position}
      className={`${top_padding_off ? 'pb-20 lg:pb-[120px]' : 'py-20 lg:py-[120px]'} grid justify-center ${headline ? 'grid-rows-[auto_auto] gap-10' : ''}`}
      data-name="video-large-autoplay"
    >
      {headline && (
        <h2
          className={`h3 sm:h2 px-2 text-center ${headline_position === 'Top' ? 'order-first' : 'order-last'}`}
        >
          {headline}
        </h2>
      )}
      <div
        ref={videoRef}
        className={`lg:max-w-container relative mx-auto h-full`}
      >
        {windowSize.width >= 640 ? (
          <>
            <Image
              src={desktop_placeholder_image.url}
              width="1200"
              height="675"
              sizes="(min-width: 1280px) 1200px, 100vw"
              alt={desktop_placeholder_image.title.replace(/\.[^/.]+$/, '')}
              className={`${videoLoaded ? 'invisible' : ''}`}
            />
            <div>
              <ReactPlayer
                key={remountKey}
                url={desktop_video_link}
                {...configuration}
                className="full-iframe-child absolute top-0 left-0"
              />
              {!use_native_controls &&
                (videoPlaying && videoLoaded ? (
                  <SvgPause
                    className="video-pause-button right-[14px] bottom-4 h-[40px] w-[40px] sm:right-[21px] sm:bottom-[18px]"
                    handleClick={handleClick}
                  />
                ) : videoLoaded ? (
                  <SvgPlay
                    className="video-play-button right-[14px] bottom-4 h-[40px] w-[40px] sm:right-[21px] sm:bottom-[18px]"
                    handleClick={handleClick}
                  />
                ) : null)}
            </div>
          </>
        ) : windowSize.width < 640 ? (
          <>
            <Image
              src={mobile_placeholder_image.url}
              width="635"
              height="635"
              sizes="100vw"
              alt={mobile_placeholder_image.title.replace(/\.[^/.]+$/, '')}
              className={`${videoLoaded ? 'invisible' : ''}`}
            />
            <ReactPlayer
              key={remountKey}
              url={mobile_video_link}
              {...configuration}
              className="full-iframe-child absolute top-0 left-0"
            />
            {!use_native_controls &&
              (videoPlaying && videoLoaded ? (
                <SvgPause
                  className="video-pause-button right-[14px] bottom-4 h-[40px] w-[40px] sm:right-[21px] sm:bottom-[18px]"
                  handleClick={handleClick}
                />
              ) : videoLoaded ? (
                <SvgPlay
                  className="video-play-button right-[14px] bottom-4 h-[40px] w-[40px] sm:right-[21px] sm:bottom-[18px]"
                  handleClick={handleClick}
                />
              ) : null)}
          </>
        ) : null}
      </div>
    </section>
  );
}
